import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import PropTypes from 'prop-types';
import c from 'classnames';
import { Helmet } from 'react-helmet';
import Lottie from 'react-lottie';

import { getStaticText } from '../../../utils/constants';
import {
    getBenchmarkString,
    getFilterArray,
    getAiOraBenchmarkDefaultForFetch,
    getAiOraModuleRecords,
    getAiOraQuartileColors,
    getAiOraModuleTypeLabel,
    isAiOraAbbreviatedSurvey,
    AI_ORA_BENCHMARK_LEGEND_KEYS,
    sortArrayOnNumber,
} from '../../../utils/functions';
import colors from '../../../sass/colors';
import FilterSvg from '../assets/Filter.svg';
import ActiveFilters1 from '../assets/ActiveFilters1.svg';
import animationData from '../assets/lottie.json';
import ErrorToast from '../../../components/InformationToast/ErrorToast';
import '../QuestionBoard/index.scss';

const hasAiOraModuleContent = ({ children = [] } = {}) => children.length > 0;

const hasAiOraNodeContent = ({ children = [], questions = [] } = {}) => children.length > 0 || questions.length > 0;

const AI_ORA_ENTERPRISE_VALUE_KEY = 'enterprise_value';

function AiOraBoard({
    toggleFilterView,
    apiParams,
    apiLoadingCount,
    activeFilter,
    errorInfo,
    filters,
    defaultSettings,
    getAiOraQuestionScore,
    aiOraQuestionScore,
    aiOraQuestionScoreFetched,
}) {
    const {
        lang,
        ohid = '',
        threshold = 10,
        report_type = '',
        survey_version = '',
        survey_meta_config = {},
        quartileColors = {},
        benchmark_year: benchmarkYear = '',
    } = defaultSettings;
    const staticText = getStaticText(lang);
    const { SITE_TEXT } = staticText;
    const {
        FILTERS,
        AI_ORA_QUES_TEXT,
        AGGREMENT_LEVEL_PERCENT,
        QUESTIONS_LEGEND,
        NOTE,
        QUESTION_NOTE,
        BENCHMARK_HEAD,
        BENCHMARK,
        YEAR,
    } = SITE_TEXT || {};

    const filterCheck = apiParams.filters || [];
    const isOhi4 = survey_version === '4' || survey_version === '3_2';
    const [expandedList, updateExpandedList] = useState([]);
    const [expandedBranches, setExpandedBranches] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const { $white, $black } = colors;

    const quartileColorsInUse = useMemo(() => getAiOraQuartileColors(aiOraQuestionScore, quartileColors), [
        aiOraQuestionScore,
        quartileColors,
    ]);
    const aiOraMetaData = aiOraQuestionScore?.meta_data || aiOraQuestionScore?.qbyq?.meta_data || {};
    const formatAiOraSize = value => {
        if (value === null || value === undefined || value === '') return '';
        const normalizedValue = Number(String(value).replace(/,/g, ''));
        return Number.isNaN(normalizedValue) ? value : normalizedValue.toLocaleString('en-US');
    };
    const aiOraPersonalSize = formatAiOraSize(aiOraMetaData.personal_size);
    const aiOraOrgSize = formatAiOraSize(aiOraMetaData.org_size);
    const aiOraBenchmarkPersonalN = formatAiOraSize(aiOraMetaData.n_respondents_personal);
    const aiOraBenchmarkOrgN = formatAiOraSize(aiOraMetaData.n_respondents_org);
    const aiOraBenchmarkName = aiOraMetaData.benchmark_name || '';
    const aiOraModuleTypeLabel = getAiOraModuleTypeLabel(survey_meta_config, aiOraMetaData);
    const isAiOraAbbreviated = isAiOraAbbreviatedSurvey(survey_meta_config) || aiOraModuleTypeLabel === 'Abbreviated';

    const aiOraModules = useMemo(() => {
        const modules = getAiOraModuleRecords(aiOraQuestionScore)
            .slice()
            .reverse();
        if (!isAiOraAbbreviated) return modules;
        return modules.filter(({ key }) => key !== AI_ORA_ENTERPRISE_VALUE_KEY);
    }, [aiOraQuestionScore, isAiOraAbbreviated]);

    const aiOraRequestSentRef = useRef(false);
    const prevAiOraFetchedRef = useRef(aiOraQuestionScoreFetched);
    const prevBenchmarkSelectionRef = useRef('');
    const { demographics = [], benchmarks: filterBenchmarks = [] } = filters[0] || {};
    const benchmarkSelection =
        getBenchmarkString(filterBenchmarks) ||
        getAiOraBenchmarkDefaultForFetch(defaultSettings, apiParams.benchmarks, {
            preferUserSelection: Boolean(apiParams.benchmarks),
        });

    useEffect(() => {
        if (prevAiOraFetchedRef.current && !aiOraQuestionScoreFetched) {
            aiOraRequestSentRef.current = false;
        }
        prevAiOraFetchedRef.current = aiOraQuestionScoreFetched;
    }, [aiOraQuestionScoreFetched]);

    useEffect(() => {
        if (prevBenchmarkSelectionRef.current !== benchmarkSelection) {
            if (!apiLoadingCount) {
                aiOraRequestSentRef.current = false;
            }
            prevBenchmarkSelectionRef.current = benchmarkSelection;
        }
    }, [benchmarkSelection, apiLoadingCount]);

    useEffect(() => {
        if (aiOraQuestionScoreFetched || aiOraRequestSentRef.current || apiLoadingCount) return;
        if (!apiParams?.ohid) return;
        if (!filterBenchmarks.length || !benchmarkSelection) return;

        const {
            ohid: apiOhid = ohid,
            filters: filterParams = [],
            lang: apiLang = lang,
            threshold: apiThreshold = threshold,
            report_type: apiReportType = report_type,
            year: apiYear = '',
        } = apiParams || {};
        const filterArray = filterParams?.length ? filterParams : getFilterArray(demographics);
        const normalizedReportType =
            String(apiReportType || '').toLowerCase() === 'percent favorable' ? 'percent' : apiReportType || 'percent';

        aiOraRequestSentRef.current = true;
        getAiOraQuestionScore({
            ohid: apiOhid,
            filters: filterArray,
            lang: Number.parseInt(apiLang, 10),
            threshold: apiThreshold,
            report_type: normalizedReportType,
            year: Number.parseInt(apiYear, 10),
            benchmarks: benchmarkSelection,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiOraQuestionScoreFetched, apiLoadingCount, apiParams, benchmarkSelection, filterBenchmarks.length]);

    const updateExpanded = index => {
        let newList = [...expandedList];
        const currentIndex = expandedList.indexOf(index);
        if (currentIndex !== -1) {
            newList = [...expandedList.slice(0, currentIndex), ...expandedList.slice(currentIndex + 1)];
        } else {
            newList.push(index);
        }
        updateExpandedList(newList);
    };

    const isBranchExpanded = branchId => expandedBranches[branchId] !== false;

    const toggleBranch = branchId => {
        setExpandedBranches(prev => ({
            ...prev,
            [branchId]: !isBranchExpanded(branchId),
        }));
    };

    const handleBranchKeyDown = (event, branchId) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleBranch(branchId);
        }
    };

    const ExpandIcon = () => (
        <div className="expand-icon">
            <Lottie options={{ loop: false, autoplay: false, animationData }} />
        </div>
    );

    const ExpandIconAnimated = () => {
        const defaultOptions = {
            loop: true,
            autoplay: true,
            animationData,
        };

        const onClickOptions = {
            ...defaultOptions,
            loop: false,
            autoplay: false,
        };

        const handleClick = () => {
            setIsExpanded(true);
        };

        return (
            <div className="expand-icon" onClick={handleClick}>
                <Lottie options={isExpanded ? onClickOptions : defaultOptions} />
            </div>
        );
    };

    const renderScoreBadge = (score, quartile = '', { showDash = false } = {}) => {
        if (showDash) {
            return (
                <div className="metaTotalScore">
                    <div
                        className="circle"
                        style={{
                            background: $white,
                            color: $black,
                            borderColor: colors.$grey200,
                            borderWidth: 1,
                            borderStyle: 'solid',
                        }}
                        aria-label="Score not available"
                    >
                        -
                    </div>
                </div>
            );
        }

        if (score === '' || score === undefined || score === null) return null;

        const { background = $white, color = $black, border = colors.$grey200 } = quartileColorsInUse[quartile] || {};
        const ohi4Border = isOhi4 && quartile !== '999';
        const useApiQuartileStyle = Boolean(
            aiOraQuestionScore?.qbyq?.meta_data?.quartile_style || aiOraQuestionScore?.meta_data?.quartile_style
        );

        return (
            <div className="metaTotalScore">
                <div
                    className="circle"
                    style={{
                        background:
                            !useApiQuartileStyle && ohi4Border
                                ? `linear-gradient(135deg, ${border} 25%, ${background} 25%)`
                                : background,
                        color,
                        borderColor: ohi4Border && !useApiQuartileStyle ? 'transparent' : border,
                        borderWidth: quartile === '' ? 1 : 2,
                        borderStyle: 'solid',
                    }}
                    aria-label={`Score ${score}`}
                >
                    {score}
                </div>
            </div>
        );
    };

    const renderBranchHeader = (node, branchId, depth) => {
        const { display_name = '', score, quartile = '' } = node;
        const isOpen = isBranchExpanded(branchId);
        const headingClass = c({ practiceHead: depth === 1 });

        return (
            <div
                className={c('practiceContent', 'aiOraBranchToggle', { aiOraBranchCollapsed: !isOpen })}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggleBranch(branchId)}
                onKeyDown={event => handleBranchKeyDown(event, branchId)}
            >
                {renderScoreBadge(score, quartile, { showDash: isAiOraAbbreviated && depth === 1 })}
                {depth === 1 ? (
                    <h3 className={headingClass}>{display_name}</h3>
                ) : (
                    <h4 className={headingClass}>{display_name}</h4>
                )}
            </div>
        );
    };

    const renderQuestionRows = (questions, listIndex) => {
        if (!questions.length) return null;
        const isNormalStringAdd = questions.some(({ qtext = '' }) => isNaN(parseInt(qtext[0], 10)));
        const addQuesInUse = isNormalStringAdd ? questions : sortArrayOnNumber(questions, 'qtext');

        return addQuesInUse.map(
            ({ meta = '', qtext = '', bottom2_score = 0, nuetral_score = 0, top2_score = 0, na_score = '' }) => {
                const totalScore = bottom2_score + nuetral_score + top2_score;
                return (
                    <section key={`${listIndex}-${meta || qtext}`} className="metaSection clearfix">
                        <div className="metaText">{qtext}</div>
                        {totalScore ? (
                            <div className={c({ ohi4Meta: isOhi4 }, 'metaScore')}>
                                <div>
                                    <span style={{ width: `${bottom2_score}%` }}>{bottom2_score}</span>
                                    <span style={{ width: `${nuetral_score}%` }}>{nuetral_score}</span>
                                    <span style={{ width: `${top2_score}%` }}>{top2_score}</span>
                                    <span className="naScore">{`${na_score}%`}</span>
                                </div>
                            </div>
                        ) : null}
                    </section>
                );
            }
        );
    };

    const renderTreeNode = (node, parentPath, depth = 1) => {
        const { key, children = [], questions = [] } = node;
        const branchId = `${parentPath}/${key}`;
        const isOpen = isBranchExpanded(branchId);

        if (!hasAiOraNodeContent(node)) return null;

        return (
            <div key={branchId} className={c({ practiceSet: depth === 1, aiOraNestedNode: depth > 1 })}>
                {renderBranchHeader(node, branchId, depth)}
                {isOpen ? (
                    <div className="aiOraBranchBody" role="group" aria-label={node.display_name}>
                        {renderQuestionRows(questions, branchId)}
                        {children.map(child => renderTreeNode(child, branchId, depth + 1))}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderBenchmarkLegend = () => (
        <ul className="benchmark clearfix fixedScoreCardSurvey aiOraBenchmarkLegend">
            <li>{BENCHMARK_HEAD} : </li>
            {AI_ORA_BENCHMARK_LEGEND_KEYS.map(quartileKey => {
                const { background = colors.$grey300, border = colors.$grey200, title = '' } =
                    quartileColorsInUse[quartileKey] || {};
                const legendTitle =
                    title ||
                    {
                        1: 'Top quartile',
                        2: 'Second quartile',
                        3: 'Third quartile',
                        4: 'Bottom quartile',
                        999: 'Benchmark not available',
                    }[quartileKey];
                const isUnavailable = quartileKey === '999';
                const swatchBackground = isUnavailable ? $white : background;
                const swatchBorder = isUnavailable ? border : background;
                return (
                    <li key={quartileKey}>
                        <span
                            style={{ background: swatchBackground, border: `1px solid ${swatchBorder}` }}
                            aria-hidden="true"
                        />
                        <div style={{ color: 'black' }}>{legendTitle}</div>
                    </li>
                );
            })}
        </ul>
    );

    const renderAiOraBenchmarkInfoRow = () => {
        if (!benchmarkYear && !aiOraBenchmarkPersonalN && !aiOraBenchmarkOrgN && !aiOraBenchmarkName) {
            return null;
        }

        return (
            <div className="filterAppliedContent">
                <div className="filterDemo">
                    <div className="heading">{BENCHMARK}:</div>
                    {benchmarkYear ? (
                        <div className="nsizeContent">
                            <div>{YEAR} : </div>
                            <div>{benchmarkYear}</div>
                        </div>
                    ) : null}
                    {aiOraBenchmarkPersonalN ? (
                        <div className="nsizeContent">
                            <div>Personal Readiness (n) :</div>
                            <div>{aiOraBenchmarkPersonalN}</div>
                        </div>
                    ) : null}
                    {aiOraBenchmarkOrgN ? (
                        <div className="nsizeContent">
                            <div>Organizational Readiness (n) :</div>
                            <div>{aiOraBenchmarkOrgN}</div>
                        </div>
                    ) : null}
                    {aiOraBenchmarkName ? (
                        <div className="filterContent">
                            <div className="filterHighlight filterKey filterLab">{aiOraBenchmarkName}</div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    const renderModuleSection = (module, index) => {
        const { display_name = '', score, quartile = '', children = [] } = module;
        if (!hasAiOraModuleContent(module)) return null;

        return (
            <Accordion classes={{ root: 'accordionRoot' }} key={module.key} expanded={expandedList.includes(index)}>
                <AccordionSummary
                    expandIcon={index === 0 ? <ExpandIconAnimated /> : <ExpandIcon />}
                    onClick={() => updateExpanded(index)}
                    classes={{ content: 'accordionHead' }}
                >
                    <div className="outcomeContent">
                        {renderScoreBadge(score, quartile, { showDash: isAiOraAbbreviated })}
                        {display_name}
                        <ul className={c({ ohi4Legends: isOhi4 }, 'questionLegends')}>
                            <li className="listTitle">{AGGREMENT_LEVEL_PERCENT}</li>
                            {QUESTIONS_LEGEND.map(legendText => (
                                <li key={legendText}>
                                    <span />
                                    {legendText}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {children.length ? (
                        <ul className="childContent">
                            {children.map(({ display_name: childName = '', key: childKey = '' }) => (
                                <li key={childKey || childName}>{childName}</li>
                            ))}
                        </ul>
                    ) : null}
                </AccordionSummary>
                <AccordionDetails classes={{ root: 'clearfix' }}>
                    <section className="leftSection">
                        <div className="clearfix">{children.map(child => renderTreeNode(child, module.key, 1))}</div>
                        <br />
                    </section>
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <>
            <div className="questionBoard clearfix">
                <Helmet>
                    <title>{AI_ORA_QUES_TEXT || 'AI-ORA'}</title>
                </Helmet>

                <div className="rightScorecardHeader">
                    <div className="scorecardHeading">
                        <div className="mainHeading">{AI_ORA_QUES_TEXT || 'AI-ORA'}</div>
                        {aiOraPersonalSize ? (
                            <div className="nRes">
                                Personal Readiness (n) : <span>{aiOraPersonalSize}</span>
                            </div>
                        ) : null}
                        {aiOraOrgSize ? (
                            <div className="nRes">
                                Organizational Readiness (n) : <span>{aiOraOrgSize}</span>
                            </div>
                        ) : null}
                        {aiOraModuleTypeLabel ? (
                            <div className="nRes aiOraAbbreviatedTile">{aiOraModuleTypeLabel}</div>
                        ) : null}
                    </div>
                    <ul className="navScoreList">
                        <li onClick={() => toggleFilterView(true)} data-testid="filterButton">
                            <img src={activeFilter && filterCheck.length > 0 ? ActiveFilters1 : FilterSvg} alt="" />
                            <span className="filter">{FILTERS}</span>
                        </li>
                    </ul>
                </div>

                {errorInfo && <ErrorToast message={errorInfo} />}

                {aiOraQuestionScoreFetched && !apiLoadingCount ? renderAiOraBenchmarkInfoRow() : null}

                {aiOraQuestionScoreFetched && !apiLoadingCount ? (
                    <Fragment>
                        {aiOraModules.map((module, index) => renderModuleSection(module, index))}
                        <div className="note">
                            <b>{NOTE}:</b>
                            <br />
                            1. {QUESTION_NOTE}
                        </div>
                    </Fragment>
                ) : null}
            </div>
            {aiOraQuestionScoreFetched && !apiLoadingCount ? renderBenchmarkLegend() : null}
        </>
    );
}

AiOraBoard.propTypes = {
    toggleFilterView: PropTypes.func.isRequired,
    apiParams: PropTypes.object.isRequired,
    apiLoadingCount: PropTypes.number.isRequired,
    activeFilter: PropTypes.bool.isRequired,
    errorInfo: PropTypes.string.isRequired,
    filters: PropTypes.array.isRequired,
    defaultSettings: PropTypes.object.isRequired,
    getAiOraQuestionScore: PropTypes.func.isRequired,
    aiOraQuestionScore: PropTypes.object.isRequired,
    aiOraQuestionScoreFetched: PropTypes.bool.isRequired,
};

export default AiOraBoard;
